<template>
  <v-file-input
    :label="`${label} (${dimensions.width}x${dimensions.height}px)`"
    :rules="rules"
    :accept="formats"
    variant="outlined"
    density="compact"
    prepend-icon=""
    prepend-inner-icon="fa-regular fa-image"
    clear-icon=""
    :clearable="false"
    :show-size="1000"
    color="primary"
    @change="onFileChange"
  />
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import type { ValidationRule } from '@/types/vuetify/ValidationRule'

export default defineComponent({
  props: {
    maxSize: {
      required: false,
      type: Number as PropType<number>,
      default: 2,
    },
    url: {
      required: false,
      type: String as PropType<string>,
      default: '',
    },
    label: {
      required: false,
      type: String as PropType<string>,
      default: 'Selecione uma imagem...',
    },
    formats: {
      required: false,
      type: Array as PropType<string[]>,
      default: () => ['image/jpeg', 'image/png', 'image/webp'],
    },
    precise: {
      required: false,
      type: Boolean as PropType<boolean>,
      default: false,
    },
    extraRules: {
      required: false,
      type: Array as PropType<ValidationRule[]>,
      default: () => [],
    },
    dimensions: {
      required: false,
      type: Object as PropType<{ width: number, height: number }>,
      default: () => ({ width: 0, height: 0 }),
    },
  },
  emits: [ 'update:modelValue' ],
  data() {
    return {
      image: undefined as undefined | File,
      rules: [
        this.requiredRule.bind(this),
        this.formatRule.bind(this),
        this.sizeRule.bind(this),
        this.dimensionsRule.bind(this),
        ...this.extraRules,
      ] as ValidationRule[],
    }
  },
  methods: {
    alreadyHasImage(): boolean {
      const pattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/\S*)?$/i
      return pattern.test(this.url) && !this.image
    },
    requiredRule(value: File): string | boolean {
      if (this.alreadyHasImage()) return true
      if (!value) return 'Este campo é obrigatório'
      return true
    },

    formatRule(value: File): string | boolean {
      if (this.alreadyHasImage()) return true
      if (!value) return true
      console.log(value)
      if (!this.formats.includes(value.type)) {
        const accepted = this.formats.map(f => f.split('/')[1]).join(', ')
        return `Apenas os formatos ${accepted} são aceitos.`
      }
      return true
    },

    sizeRule(value: File): string | boolean {
      if (this.alreadyHasImage()) return true
      if (!value) return true
      const valid = this.validateImageSize(value)
      return valid
        ? true
        : `A imagem não deve exceder o tamanho máximo: ${this.maxSize} MB`
    },

    dimensionsRule(value: File): Promise<boolean | string> {
      if (this.alreadyHasImage()) return Promise.resolve(true)
      if (!value) return Promise.resolve(true)
      return this.validateImageDimensionsAsync(value)
    },
    onFileChange(event: Event) {
      const files = (event.target as HTMLInputElement).files
      if (files && files.length > 0) {
        this.image = files[0]
        this.$emit('update:modelValue', this.image)
      }
    },
    validateImageSize(file: File): boolean {
      // INFO: Conversion of MB to Bytes from a scalar value. Default is 2 Mb.
      const maxSize = this.maxSize * 1024 * 1024
      return !file || file.size <= maxSize
    },
    validateImageFormat(file: File): boolean {
      return !file || this.formats.includes(file.type)
    },
    isExact(width: number, height: number) {
      return width === this.dimensions.width && height === this.dimensions.height
    },
    isAspectRatioCorrect(width: number, height: number, aspectRatio: number) {
      return Math.abs(width / height - aspectRatio) < 0.01
    },
    isWithinMaxDimensions(width: number, height: number) {
      return width <= this.dimensions.width && height <= this.dimensions.height
    },
    async validateImageDimensionsAsync(file: File): Promise<boolean | string> {
      if (!file) return false

      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            const isValid = this.validateDimensions(img.width, img.height)
            resolve(isValid ? true : `A imagem deve ter ${this.precise ? 'exatamente' : 'no máximo'} ${this.dimensions.width}x${this.dimensions.height} pixels`)
          }
          img.src = e.target?.result as string
        }
        reader.readAsDataURL(file)
      })
    },
    validateDimensions(width: number, height: number): boolean {
      return this.precise ? this.isExact(width, height) : this.isWithinMaxDimensions(width, height)
    },
  },
})
</script>
